import React, { ReactNode } from 'react'
import classNames from 'classnames'
import dynamic from 'next/dynamic'
import { usePlayerPreference } from '@/hooks/usePlayerPreference'
import { useUser } from '@/services/UserService'
import { useStrictModeDetector } from '@/services/WatchableService/hooks'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeAnalytics } from '@/utils/analytics'
import { getAnalyticsSessionId } from '@/utils/analytics/session-utils'
import { AngelPlayerContextProvider } from './AngelPlayerContext'
import { BitmovinPlayer } from './BitmovinPlayer'
import { Asset, PlayerOverlay, SmpteController } from './types'

const HlsPlayer = dynamic(() => import('./HlsPlayer/HlsPlayer').then((mod) => ({ default: mod.HlsPlayer })), {
  ssr: false,
})

export interface AngelPlayerProps {
  autoplay?: boolean
  asset: Asset | null | undefined
  overlays?: PlayerOverlay[]
  smpteTimestamp?: string
  className?: string
  aspectClassName?: string
  children?: ReactNode | undefined
  playerContainerClassName?: string
  onStart?: () => void
  onControllerInit?: (controller: SmpteController) => void
  shouldSaveWatchPosition?: boolean
  isMuted?: boolean
  onPlaybackFinished?: () => void
}

export const AngelPlayer: React.FC<AngelPlayerProps> = ({
  autoplay,
  asset,
  overlays,
  smpteTimestamp,
  children,
  className,
  aspectClassName,
  playerContainerClassName,
  onStart,
  shouldSaveWatchPosition = true,
  onControllerInit,
  onPlaybackFinished,
  isMuted,
  ...rest
}) => {
  const { userId } = useUser()
  const analytics = useSafeAnalytics()
  const isStrict = useStrictModeDetector()
  const { locale } = useLocale()
  const sessionId = getAnalyticsSessionId()
  const playerPreference = usePlayerPreference()

  return asset ? (
    <AngelPlayerContextProvider
      asset={asset}
      smpteTimestamp={smpteTimestamp}
      onStart={onStart}
      shouldSaveWatchPosition={shouldSaveWatchPosition}
      onControllerInit={onControllerInit}
      onPlaybackFinished={onPlaybackFinished}
    >
      <div data-testid="angel-player" className={classNames('angel-player', className)}>
        <div className={playerContainerClassName}>
          {playerPreference === 'hlsjs' ? (
            <HlsPlayer
              asset={asset}
              autoplay={autoplay}
              overlays={overlays}
              aspectClassName={aspectClassName}
              isMuted={isMuted}
              enableAds
              anonymousUserId={analytics?.user()?.anonymousId}
              loggedInUserId={userId}
              sessionId={sessionId}
            />
          ) : (
            <BitmovinPlayer
              autoplay={autoplay}
              asset={asset}
              overlays={overlays}
              aspectClassName={aspectClassName}
              anonymousUserId={analytics?.user()?.anonymousId}
              loggedInUserId={userId}
              sessionId={sessionId}
              enableAds
              isStrict={isStrict}
              uiLanguage={locale}
              isMuted={isMuted}
              {...rest}
            />
          )}
          {children}
        </div>
      </div>
    </AngelPlayerContextProvider>
  ) : null
}
